/**
 * Multi-domain host helpers.
 *
 * Velko POS is served from four distinct hostnames in production:
 *   - velko-pos.com       → marketing site + auth pages (/login, /register)
 *   - app.velko-pos.com   → owner application (/owner/*, /pos)
 *   - admin.velko-pos.com → back-office (/admin/*)
 *   - api.velko-pos.com   → Laravel backend (/api/*, /sanctum/*)
 *
 * The frontend is a single Vite bundle deployed to all three "front" hosts —
 * Nginx multiplexes by `Host` header. Cross-host navigation MUST go through a
 * full browser navigation (`window.location.href`), not React Router, because
 * Router only owns the in-host history.
 *
 * Hosts are read from `VITE_*_URL` env vars at build time. When unset (dev),
 * we fall back to `window.location.origin` so a single host (`localhost:3000`)
 * still answers for all 4 roles.
 */

const fallbackOrigin = (): string => {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return 'http://localhost:3000';
};

const stripTrailingSlash = (value: string): string => value.replace(/\/+$/, '');

const resolve = (value: string | undefined): string =>
  stripTrailingSlash(value || fallbackOrigin());

export const HOSTS = {
  marketing: resolve(import.meta.env.VITE_MARKETING_URL as string | undefined),
  app: resolve(import.meta.env.VITE_APP_URL as string | undefined),
  admin: resolve(import.meta.env.VITE_ADMIN_URL as string | undefined),
  api: resolve(import.meta.env.VITE_API_URL as string | undefined),
  // Storefront public des pages commerciales (shop.velko-pos.com).
  store: resolve(import.meta.env.VITE_STORE_URL as string | undefined),
} as const;

const join = (base: string, path: string): string => {
  if (!path) return base;
  return `${base}/${path.replace(/^\/+/, '')}`;
};

export const marketingUrl = (path = ''): string => join(HOSTS.marketing, path);
/**
 * URL publique storefront. En local (VITE_STORE_URL absent), le storefront est
 * multiplexé sous /shop/* sur le host unique.
 */
export const storeUrl = (path = ''): string => {
  if (currentHostRole() === 'local' || !import.meta.env.VITE_STORE_URL) {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000';
    return `${origin}/shop${path.startsWith('/') ? path : `/${path}`}`;
  }
  return join(HOSTS.store, path);
};
export const appUrl = (path = ''): string => join(HOSTS.app, path);
export const adminUrl = (path = ''): string => join(HOSTS.admin, path);
export const apiUrl = (path = ''): string => join(HOSTS.api, path);

/**
 * Returns the role implied by the current hostname:
 *   - `app`       when running on app.velko-pos.com
 *   - `admin`     when running on admin.velko-pos.com
 *   - `marketing` for velko-pos.com
 *   - `local`     for localhost / 127.0.0.1 / *.local
 *
 * The `local` role bundles all 3 trees into one with the legacy `/owner/` and
 * `/admin/` prefixes so a single dev server (`localhost:3000` / `:8080`) can
 * serve every role at once. Prod / staging use the strict subdomain split.
 */
export type HostRole = 'marketing' | 'app' | 'admin' | 'storefront' | 'local';

const LOCAL_HOST_PATTERNS = /^(localhost|127\.0\.0\.1|::1|0\.0\.0\.0)$|\.local$|\.lan$/i;

export const currentHostRole = (hostname?: string): HostRole => {
  const host = (
    hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '')
  ).toLowerCase();
  if (!host) return 'local';

  // Local dev: single host serves every role with the legacy /owner/ /admin/ prefixes.
  if (LOCAL_HOST_PATTERNS.test(host)) return 'local';

  if (host === new URL(HOSTS.app).hostname) return 'app';
  if (host === new URL(HOSTS.admin).hostname) return 'admin';
  if (import.meta.env.VITE_STORE_URL && host === new URL(HOSTS.store).hostname) return 'storefront';
  if (host === new URL(HOSTS.marketing).hostname) return 'marketing';

  // Heuristic fallback for staging or unknown subdomains.
  if (host.startsWith('app.') || host.startsWith('app-')) return 'app';
  if (host.startsWith('admin.') || host.startsWith('admin-')) return 'admin';
  if (host.startsWith('shop.') || host.startsWith('shop-')) return 'storefront';
  return 'marketing';
};

/**
 * Build the absolute URL of a given path on the host it canonically lives on.
 *
 * Since v0.5.15 the /owner/ and /admin/ prefixes are dropped — routes live at
 * the root of their respective subdomain. Legacy /owner/* and /admin/* paths
 * are accepted here for retro-compatibility (callers may still pass them);
 * they get rewritten to the prefix-less form on the canonical host.
 *
 * Examples:
 *   canonicalUrl('/login')           → https://velko-pos.com/login
 *   canonicalUrl('/dashboard')       → https://app.velko-pos.com/dashboard
 *   canonicalUrl('/owner/dashboard') → https://app.velko-pos.com/dashboard   (legacy strip)
 *   canonicalUrl('/admin')           → https://admin.velko-pos.com/
 *   canonicalUrl('/admin/users')     → https://admin.velko-pos.com/users     (legacy strip)
 */
/**
 * Strips legacy `/owner` prefix from in-host paths for `app.velko-pos.com`.
 * E.g. `/owner/inventory/stocktake` -> `/inventory/stocktake`
 *      `/owner` -> `/`
 */
export const stripLegacyOwnerPrefix = (path: string): string => {
  if (path === '/owner' || path.startsWith('/owner/')) {
    return path.replace(/^\/owner/, '') || '/';
  }
  return path;
};

/**
 * Strips legacy `/admin` prefix from in-host paths for `admin.velko-pos.com`.
 * E.g. `/admin/users` -> `/users`
 *      `/admin` -> `/`
 */
export const stripLegacyAdminPrefix = (path: string): string => {
  if (path === '/admin' || path.startsWith('/admin/')) {
    return path.replace(/^\/admin/, '') || '/';
  }
  return path;
};

export const canonicalUrl = (path: string): string => {
  const role = currentHostRole();

  // Local dev: single host serves every role with legacy prefixes preserved.
  if (role === 'local') {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000';
    return `${origin}${path}`;
  }

  // Production / staging: strict subdomain mapping. Legacy /owner/* and /admin/*
  // are stripped — the bare path (e.g. /dashboard, /users) lives at the root of
  // the dedicated subdomain.
  if (path === '/owner' || path.startsWith('/owner/')) {
    return appUrl(stripLegacyOwnerPrefix(path));
  }
  if (path === '/pos' || path.startsWith('/pos/')) {
    return appUrl(path);
  }
  if (path === '/admin' || path.startsWith('/admin/')) {
    return adminUrl(stripLegacyAdminPrefix(path));
  }
  // Auth pages (login/register/etc.) and marketing pages stay on the marketing host.
  return marketingUrl(path);
};

/**
 * Paths that are NEVER prefixed with /owner/ in local mode, because the local
 * route tree exposes them at the root (they are not owner-scoped, or they live
 * outside the owner tree like the POS):
 *   - /pos, /pos/*           — POS app, has its own layout
 *   - /login, /register      — auth flows shared with marketing
 *   - /two-factor-challenge  — auth flow
 *   - /forgot-password       — auth flow
 *   - /reset-password/*      — auth flow
 *   - /verify-email          — auth flow
 *   - /onboarding            — actually lives at /owner/onboarding, NOT here
 */
const OWNER_PASSTHROUGH_PATHS =
  /^\/(pos(\/.*)?|login(\/.*)?|register|two-factor-challenge|forgot-password|reset-password(\/.*)?)$/;

/**
 * Returns the URL of an owner-tree path on the local dev server, where every
 * role is multiplexed under a single host with the legacy `/owner/` prefix.
 *
 * In production/staging this is a no-op — owner routes live at the root of
 * `app.velko-pos.com` (no prefix). The helper is meant to wrap the `to=` prop
 * of `<Link>` and the route matching logic of sidebar/topbar components, so
 * that a single source of truth (`shopNavItems`, hard-coded paths) renders
 * correctly in both modes.
 *
 * @example
 *   ownerPath('/dashboard')   // local: '/owner/dashboard', prod: '/dashboard'
 *   ownerPath('/pos')         // local: '/pos',             prod: '/pos'
 *   ownerPath('/login')       // local: '/login',           prod: '/login'
 *   ownerPath('/owner/foo')   // returns '/owner/foo' (no double prefix)
 */
export const ownerPath = (path: string): string => {
  if (currentHostRole() !== 'local') return path;
  if (!path.startsWith('/')) return path;
  if (path === '/owner' || path.startsWith('/owner/')) return path;
  if (OWNER_PASSTHROUGH_PATHS.test(path)) return path;
  if (path === '/') return '/owner';
  return `/owner${path}`;
};

/** Return true when pathname is the route or a nested route below target. */
export const isNavPathActive = (pathname: string, targetPath: string): boolean =>
  pathname === targetPath || pathname.startsWith(`${targetPath}/`);

/**
 * Resolve the most specific active navigation target. This prevents a broad
 * route such as `/products` from winning over `/products/ingredients`.
 */
export const mostSpecificNavPath = (
  pathname: string,
  targetPaths: readonly string[],
): string | null => {
  const matches = targetPaths.filter((target) => isNavPathActive(pathname, target));
  return matches.sort((a, b) => b.length - a.length)[0] ?? null;
};

/**
 * Returns the URL of an admin-tree path on the local dev server. In prod the
 * admin tree lives at the root of `admin.velko-pos.com`, so this is a no-op.
 *
 * @example
 *   adminPath('/users')      // local: '/admin/users',  prod: '/users'
 *   adminPath('/login')      // local: '/admin/login',  prod: '/login'
 *   adminPath('/admin/foo')  // returns '/admin/foo' (no double prefix)
 */
export const adminPath = (path: string): string => {
  if (currentHostRole() !== 'local') return path;
  if (!path.startsWith('/')) return path;
  if (path === '/admin' || path.startsWith('/admin/')) return path;
  if (path === '/') return '/admin';
  return `/admin${path}`;
};
